import { useState, useEffect, ReactNode } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import type { AuthUser } from 'aws-amplify/auth';
import { motion } from 'framer-motion';

interface RequireApprovalProps {
  user: AuthUser | undefined;
  children: ReactNode;
  pendingComponent: ReactNode;
}

type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'loading' | 'error';

/**
 * RequireApproval Wrapper Component
 *
 * This component checks the user's `custom:approval_status` attribute from Cognito.
 * - If approved: renders children (the main application)
 * - If pending/rejected: renders the pendingComponent
 * - If loading: shows a loading spinner
 * - If error: shows error message with retry option
 */
export default function RequireApproval({
  user,
  children,
  pendingComponent,
}: RequireApprovalProps) {
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  const checkApprovalStatus = async () => {
    if (!user) {
      setApprovalStatus('error');
      setError('No user found');
      return;
    }

    try {
      setApprovalStatus('loading');
      setError(null);

      const attributes = await fetchUserAttributes();
      const status = attributes['custom:approval_status'] as string;

      if (status === 'approved') {
        setApprovalStatus('approved');
      } else if (status === 'rejected') {
        setApprovalStatus('rejected');
      } else {
        // Default to pending for any other value or if not set
        setApprovalStatus('pending');
      }
    } catch (err) {
      console.error('Error fetching user attributes:', err);
      setApprovalStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to verify approval status');
    }
  };

  useEffect(() => {
    checkApprovalStatus();
  }, [user]);

  // Loading state
  if (approvalStatus === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="inline-block">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-12 h-12 border-4 border-leather border-t-brass rounded-full"
            />
          </div>
          <p className="mt-4 text-text-muted uppercase tracking-wide text-sm">
            Verifying account status...
          </p>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (approvalStatus === 'error') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto p-8"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-display text-leather mb-2">
            Unable to Verify Account
          </h2>
          <p className="text-text-muted mb-6">{error}</p>
          <button
            onClick={checkApprovalStatus}
            className="btn-primary"
          >
            Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  // Approved - render main content
  if (approvalStatus === 'approved') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.div>
    );
  }

  // Pending or Rejected - render pending component
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {pendingComponent}
    </motion.div>
  );
}

/**
 * Custom hook to get approval status
 * Can be used in other components that need to check status
 */
export function useApprovalStatus() {
  const [status, setStatus] = useState<ApprovalStatus>('loading');
  const [attributes, setAttributes] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const attrs = await fetchUserAttributes();
        setAttributes(attrs as Record<string, string>);
        const approvalStatus = attrs['custom:approval_status'] as string;
        setStatus(approvalStatus === 'approved' ? 'approved' : 'pending');
      } catch (err) {
        console.error('Error fetching approval status:', err);
        setStatus('error');
      }
    };

    fetchStatus();
  }, []);

  return { status, attributes, isApproved: status === 'approved' };
}
